/**
 * Everything the new case study flow asks and offers, in one place: the questions in order, their answers,
 * and what each answer means for the story. Copy is the Paper file "Deck 2.0 Guri", page Flow (1.1 to 1.10
 * and 2B); the components hold no copy and no lists.
 */
import type { ArtId } from './art';

export const STEPS = ['about', 'use', 'work'] as const;
export type CaseStep = (typeof STEPS)[number];

export function parseCaseStep(value: string | null | undefined): CaseStep {
  return STEPS.find((step) => step === value) ?? 'about';
}

/** The key that picks the answer at this position: A, B, C... */
export const letterOf = (index: number) => String.fromCharCode(65 + index);

export interface Answer<Id extends string> {
  id: Id;
  title: string;
  art: ArtId;
  /** This answer opens a box to say it in one's own words instead of picking. */
  ownWords?: boolean;
}

/** What it is about. The ids are the brain's shapes (`deck-cases/brain/kinds.js`). */
export const SHAPES = [
  { id: 'feature', title: 'A feature I shipped', art: 'feature' },
  { id: 'problem', title: 'A problem I found', art: 'problem' },
  { id: 'redesign', title: 'A redesign', art: 'redesign' },
  { id: 'mistake', title: 'A mistake we made', art: 'mistake' },
  { id: 'how', title: 'How we did it', art: 'how' },
  { id: 'lesson', title: 'A lesson I learned', art: 'lesson' },
  { id: 'decision', title: 'Behind a decision', art: 'decision' },
  { id: 'other', title: 'Something else', art: 'speech', ownWords: true },
] as const satisfies readonly Answer<string>[];
export type ShapeId = (typeof SHAPES)[number]['id'];

/** Where it will be used, which decides who reads it and how deep it goes. */
export const USES = [
  { id: 'interview', title: 'In an interview', art: 'interview', audience: 'hiring_manager', depth: 'deep_dive' },
  { id: 'portfolio', title: 'On my portfolio', art: 'portfolio', audience: 'recruiters', depth: 'highlight' },
  { id: 'team', title: 'With my team', art: 'team', audience: 'team', depth: 'deep_dive' },
  { id: 'elsewhere', title: 'Somewhere else', art: 'speech', audience: 'other', depth: 'highlight', ownWords: true },
] as const satisfies readonly (Answer<string> & { audience: string; depth: string })[];
export type UseId = (typeof USES)[number]['id'];

/** How deep it goes, the brain's two depths (`deck-cases/brain/kinds.js`). Asked after where it will be used. */
export const DEPTHS = [
  { id: 'highlight', title: 'A highlight', art: 'highlight' },
  { id: 'deep_dive', title: 'A deep dive', art: 'deepDive' },
] as const satisfies readonly Answer<string>[];
export type DepthId = (typeof DEPTHS)[number]['id'];

/** What they do, with the ids Deck 2.0's welcome asks with, so both keep the same answers. */
export const ROLES = [
  { id: 'product_designer', title: 'Product designer', art: 'productDesigner' },
  { id: 'ux_designer', title: 'UX designer', art: 'uxDesigner' },
  { id: 'design_lead', title: 'Design lead', art: 'designLead' },
  { id: 'other', title: 'Something else', art: 'speech', ownWords: true },
] as const satisfies readonly Answer<string>[];
export type RoleId = (typeof ROLES)[number]['id'];

/**
 * What a sentence written in one's own words sounds like, by plain keywords, first match wins. It only
 * offers a guess the person can wave away ("Not quite"); it never decides for them.
 */
export const SOUNDS_LIKE: readonly (readonly [ShapeId, RegExp])[] = [
  ['redesign', /\b(redesign\w*|rebuil\w*|revamp\w*|overhaul\w*)\b/i],
  ['mistake', /\b(mistake|wrong|fail\w*|broke|regret\w*)\b/i],
  ['decision', /\b(decid\w*|decision|chose|choice|trade-?off)\b/i],
  ['lesson', /\b(learn\w*|lesson|taught|reali[sz]\w*)\b/i],
  ['problem', /\b(found|discover\w*|noticed|research\w*|uncover\w*)\b/i],
  ['feature', /\b(shipp?\w*|launch\w*|built|release\w*)\b/i],
  ['how', /\b(moved|migrat\w*|process|system|workflow|rolled out|set up)\b/i],
];

export function soundsLike(text: string): ShapeId | null {
  return SOUNDS_LIKE.find(([, pattern]) => pattern.test(text))?.[0] ?? null;
}

/** The same for who will read it, when "Somewhere else" is said in one's own words. */
export const USE_SOUNDS_LIKE: readonly (readonly [UseId, RegExp])[] = [
  ['interview', /\b(hiring|interview\w*|manager|lead|head of)\b/i],
  ['portfolio', /\b(recruit\w*|portfolio|site|website)\b/i],
  ['team', /\b(team|leadership|boss|ceo|founder\w*|stakeholder\w*)\b/i],
];

/** And for what they do. */
export const ROLE_SOUNDS_LIKE: readonly (readonly [RoleId, RegExp])[] = [
  ['design_lead', /\b(lead|head|director|manager|principal|vp)\b/i],
  ['ux_designer', /\b(ux|research\w*|usability|interaction)\b/i],
  ['product_designer', /\b(product|app|ui)\b/i],
];

/** The questions "Something else" can be said for. */
export type OwnQuestion = 'about' | 'use' | 'role';

/** "Something else", per question: what the box asks, how it can start, and whether it asks why. */
export const OWN_WORDS = {
  about: {
    placeholder: "Say it the way you'd tell a friend.",
    starters: ['I redesigned', 'We found out', 'I convinced the team to', 'We shipped'],
    why: true,
    soundsLike: (text: string): string | null => soundsLike(text),
  },
  use: {
    placeholder: "Who's going to read it?",
    starters: ['A design lead at', "Founders I'm pitching", 'My own team at'],
    why: false,
    soundsLike: (text: string): string | null => USE_SOUNDS_LIKE.find(([, pattern]) => pattern.test(text))?.[0] ?? null,
  },
  role: {
    placeholder: 'What do you do?',
    starters: ['Founding designer at', 'Design engineer', 'Brand designer'],
    why: false,
    soundsLike: (text: string): string | null => ROLE_SOUNDS_LIKE.find(([, pattern]) => pattern.test(text))?.[0] ?? null,
  },
} as const satisfies Record<OwnQuestion, unknown>;

/** A sentence of fewer words than this says too little to go on. */
export const MIN_OWN_WORDS = 3;

/**
 * What Jev (TypeSafe's System One model) is asked about a sentence in one's own words, from the intake
 * prototype (`docs/case-studies/intake-prototype.html`). After each pause it answers up to three things in
 * one call: which kind of case study it sounds like, which prepared phrase continues it, and whether it
 * already says why. Jev only picks from these lists, it never writes, and nothing below its bar shows.
 * Without a key on the server the flow falls back to `soundsLike`.
 */
export const JEV = {
  context: 'A product designer is starting to describe a portfolio case study',
  questions: {
    about: {
      ask: 'Which kind of design case study is the designer describing?',
      kinds: {
        feature: 'A feature they designed that shipped, and what it let people do',
        problem: 'A problem they noticed and showed was real',
        redesign: 'Reworking something that already existed: the old next to the new',
        mistake: 'Something that went wrong, what it cost, and what changed after',
        how: 'How the team did something: the way of working is the story',
        lesson: 'A lesson the designer learned and what taught it',
        decision: 'One decision: what was on the table and why one option won',
      } satisfies Partial<Record<ShapeId, string>>,
      next: true,
      why: true,
    },
    use: {
      ask: 'Who is going to read this case study?',
      kinds: {
        interview: 'Someone hiring designers, or an interviewer',
        portfolio: 'Recruiters or visitors skimming a portfolio site',
        team: "The designer's own team, managers or company leadership",
      } satisfies Partial<Record<UseId, string>>,
      next: false,
      why: false,
    },
    role: {
      ask: "What is the designer's role?",
      kinds: {
        product_designer: 'Designs product: apps, flows and features',
        ux_designer: 'Research, usability and interaction design',
        design_lead: 'Leads a design team or the design of a product',
      } satisfies Partial<Record<RoleId, string>>,
      next: false,
      why: false,
    },
  },
  // after "I" or "We", then after the verb, then mid-sentence to pull the story forward
  next: [
    'redesigned', 'led', 'found out', 'shipped', 'convinced the team to', 'rebuilt', 'noticed', 'decided to', 'learned', 'moved',
    'ran research on', 'simplified', 'launched', 'fixed',
    'the onboarding', 'the checkout', 'the dashboard', 'the design system', 'the mobile app', 'the pricing page', 'the signup flow',
    'the search', 'the navigation', 'the settings', 'the editor', 'our internal tools', 'how we', 'that users', 'a new', 'the whole',
    'because', 'so that', 'for new users', 'for our customers', 'after we found', 'when we saw', 'which led to', 'and it', 'to help',
    'from scratch', 'so people could', 'that nobody used',
  ],
  // Jev is a classifier, so on open text it is rarely sure of one phrase: a phrase shows when it is sure,
  // or when its top pick clearly leads the rest. The why shows when the sentence scores low on saying why.
  bars: { kind: 0.55, next: 0.45, lead: 0.3, margin: 0.15, why: 0.35 },
} as const;

export const COPY = {
  about: {
    title: "What's this case study about?",
    lede: "Pick the closest one. We'll get specific after.",
    hint: ['Press', { key: 'A' }, 'to', { key: 'H' }, 'or click'],
  },
  ownWords: {
    lede: 'In your own words. A sentence is enough.',
    startWith: 'Start with',
    soundsLike: 'Sounds like',
    notQuite: 'Not quite',
    kept: "Got it. I'll keep it in your words.",
    whyNote: 'Why it mattered helps me most.',
    tab: 'Tab',
    close: 'Back to the list',
    send: 'Continue',
    hint: [{ key: 'Enter' }, 'continues', { key: 'Esc' }, 'back to the list'],
  },
  use: {
    title: 'Where will you use it?',
    lede: "I'll shape the length and the focus for whoever reads it there.",
    hint: [{ key: 'Backspace' }, 'or the chip goes back'],
  },
  depth: {
    title: 'How deep should it go?',
    lede: 'You can make the other one later.',
    hint: [{ key: 'Backspace' }, 'or the chip goes back'],
  },
  story: {
    back: 'Sources',
    reading: 'Reading your work',
    title: 'Finding the story in what you picked',
    opening: 'Opening your sources',
    together: 'Putting it together',
    storyboard: 'Storyboard',
    filling: 'Filling in as I read',
    boardMeta: (chapters: number, frames: number, open: number) =>
      [`${chapters} chapters`, `${frames} frames`, open ? `${open} to answer` : ''].filter(Boolean).join(' · '),
    frames: (n: number) => `${n} ${n === 1 ? 'frame' : 'frames'}`,
    readIn: (n: number, seconds: number) => `Read ${n} ${n === 1 ? 'source' : 'sources'} in ${seconds}s`,
    facts: (n: number) => `${n} ${n === 1 ? 'fact' : 'facts'}`,
    nothingNew: 'Nothing new in here.',
    checked: (n: number) => `${n} facts checked. Every number comes from one of your files.`,
    seeSoFar: 'What I see so far',
    three: { problem: 'The problem', approach: 'What you did', outcome: 'What came of it' },
    isIt: 'Is that the story?',
    right: "That's right",
    off: 'Not quite',
    rightNext: (n: number) => (n ? `Good. ${n === 1 ? 'One thing' : `${n} things`} only you can answer.` : 'Good. I have what I need.'),
    rightLede: 'They are the dashed frames on the right. About two minutes, and then I write the story.',
    startFirst: 'Start with the first one',
    offNext: "What's off?",
    offLede: "Say it in your own words. I'll fix the title and the three lines, then the storyboard follows.",
    say: 'Or tell me what I got wrong',
    sayOff: 'Tell me what I got wrong',
    fixSoon: 'Fixing it from what you say comes with the next step.',
    yours: 'Yours to answer',
    answered: 'Your answer',
    failedTitle: 'I could not read this one',
    failedLede: 'There was not enough in what you picked to find the story yet. Add notes, screens or pages about this work, and read again.',
    failedRun: (reason: string) => `Your Claude stopped before it finished: ${reason}`,
    failedBack: 'Back to your picks',
    /** What the run says while it works, in the designer's words. */
    lines: {
      reading: (name: string) => `Reading ${name}`,
      looking: 'Looking over what you gave me',
      ready: (n: number) => `Got your ${n} ${n === 1 ? 'file' : 'files'} ready`,
      up: 'Your Claude is up. It can only read.',
      asking: 'Asking Deck what to look for',
      task: 'Deck handed over what to look for',
      handing: (n: string) => `Handing in ${n} facts`,
      handingBeats: (n: string) => `Handing in ${n} beats`,
      writing: 'Opening the writing step',
      accepted: 'Every fact checked against your files',
      preview: (tool: string) => `${tool} did not answer in time, so this one is read from its preview.`,
      refused: 'A few facts did not match your files. Fixing them.',
    },
    /** The viewfinder: one frame of the storyboard, big. */
    frameOf: (n: number, total: number) => `Frame ${n} of ${total}`,
    allFrames: 'All frames',
    yoursEyebrow: (chapter: string) => `${chapter} · Yours to answer`,
    askHint: 'Say it in the box below. A sentence or two is enough.',
    notInFiles: 'Not in your files yet. Your answer goes in as you say it.',
    from: (name: string) => `From ${name}`,
    answerHere: 'Your answer',
    answerMore: 'A little more',
    skip: 'Skip',
    skipped: 'Skipped',
    previous: 'Previous',
    next: 'Next',
    /** After the questions: writing the story. */
    allAnswered: "Good. That's everything I need.",
    allAnsweredLede: 'I write the story from your files and your answers, in your words.',
    writeIt: 'Write my story',
    writingEyebrow: 'Writing your story',
    writeStarting: 'Starting to write',
    storyEyebrow: 'Your story',
    storyMeta: (chapters: number, beats: number) => `${chapters} chapters · ${beats} beats`,
    playIt: 'Play the story',
    /** The story, played full screen one beat at a time. */
    player: {
      watch: 'Watch the story',
      minutes: (n: number) => `${n} min`,
      orPress: 'or press',
      space: 'space',
      chapterOf: (n: number, total: number) => `Chapter ${n} of ${total}`,
      placeOf: (n: number, total: number, chapter: string) => `${n} of ${total} · ${chapter}`,
      now: 'now',
      theProblem: 'The problem',
      wentHere: 'I went here',
      why: 'Why',
      before: 'Before',
      after: 'After',
      points: 'pts',
      inFifty: (n: number) => `${n} in 50`,
      from: (name: string) => `From ${name}`,
      keep: "what I'd keep",
      change: "what I'd change",
      me: 'Me',
      addScreen: 'Add this screen',
      thanks: 'Thanks for watching.',
      done: 'Back to my story',
      replay: 'Replay',
      yourStory: 'Your story',
      close: 'Close',
      play: 'Play',
      pause: 'Pause',
      previous: 'Previous',
      next: 'Next',
    },
    writeFailed: 'I could not write it this time. Your answers are kept, so try again.',
    tryAgain: 'Try again',
    /** The label above a fact that is said, not counted. */
    kinds: { quote: 'In their words', decision: 'Decision', event: 'What happened', line: 'What I saw', number: 'A number', result: 'The result' } as Record<string, string>,
  },
  work: {
    title: "Where's the work?",
    lede: 'Pick everything this story should read.',
    search: 'Search your work',
    more: 'Add more',
    searchInside: (unit: string) => `Search ${unit}`,
    recent: 'Recent',
    none: (query: string) => `Nothing matches “${query}”.`,
    noParts: (unit: string, query: string) => `No ${unit} match “${query}”.`,
    loading: 'Looking…',
    choose: 'Choose a folder…',
    chooseWhere: 'On your Mac',
    chooseTitle: 'Pick a folder on your Mac',
    chooseLede: 'Deck reads only the folders you pick, and only when you ask it to.',
    whole: (noun: string) => `The whole ${noun}`,
    all: (unit: string) => `All ${unit}`,
    nothing: 'Nothing picked yet',
    picked: (n: number) => `${n} picked`,
    allIn: (count: string) => `All ${count} are in`,
    someIn: (n: number, count: string) => `${n} of ${count} picked`,
    addAll: 'Add all',
    remove: 'Remove',
    add: 'Add',
    open: 'Open',
    back: 'Back',
    pick: 'Pick',
    done: 'Done',
    addWhole: (noun: string) => `Add the whole ${noun}`,
    addAllOf: (unit: string) => `Add all ${unit}`,
    read: (n: number) => `Read ${n}`,
    readSoon: 'Reading what you picked comes with the next step.',
    gathering: 'Getting what you picked ready to read…',
    nothingToRead: 'None of these can be read yet. A reading takes text files, pictures, and pages from your tools.',
    readerDown: 'The reading lab is not running on this Mac. Start it in deck-cases with node app/viewer.js.',
    macOnly: 'Your folders are searched in Deck for Mac.',
    /** The connected tools, as the right side of a row names them. */
    tools: { figma: 'Figma', notion: 'Notion', linear: 'Linear', github: 'GitHub', gmail: 'Gmail', web: 'Web' },
    /** Why a tool is missing from what is shown. Each takes the tool's name. */
    notes: {
      slow: (tool: string) => `${tool} took too long, so these are the other results.`,
      failed: (tool: string) => `${tool} did not answer this time.`,
      reconnect: (tool: string) => `Reconnect ${tool} under Integrations to search it again.`,
      limited: (tool: string) => `${tool}'s read limit for this account was reached. View and Collab seats get very few reads a month.`,
      notConnected: (tools: string) => `Connect ${tools} under Integrations to search them too.`,
      paste: 'Paste a link to a page, a Notion doc or a Figma file.',
    },
    /** The words for the parts of each kind of thing in a tool. */
    units: {
      figma: ['frame', 'frames'],
      page: ['section', 'sections'],
      project: ['issue', 'issues'],
      issue: ['sub-issue', 'sub-issues'],
      repo: ['pull request', 'pull requests'],
      pull: ['file', 'files'],
      thread: ['message', 'messages'],
      site: ['section', 'sections'],
    },
    /** The label on the right of a part's row, by its kind. */
    partLabels: { section: 'Section', frame: 'Frame', message: 'Message' },
    nouns: { figma: 'file', page: 'page', project: 'project', issue: 'issue', repo: 'repository', pull: 'pull request', thread: 'thread', site: 'page' },
    preview: {
      subIssues: 'Sub-issues',
      issues: 'Issues',
      filesChanged: 'Files changed',
      pulls: 'Pull requests',
      noBody: 'No description.',
      merged: 'Merged',
      open: 'Open',
      closed: 'Closed',
    },
    removePick: (name: string) => `Remove ${name}`,
    /** A day's worth of one kind of file, gathered into one row: "Screenshots from today". */
    group: (what: string, day: string) => `${what} from ${day}`,
    groupOf: { screenshots: 'Screenshots', videos: 'Videos' },
    today: 'today',
    yesterday: 'yesterday',
    images: (n: number) => `${n} ${n === 1 ? 'image' : 'images'}`,
    videos: (n: number) => `${n} ${n === 1 ? 'video' : 'videos'}`,
  },
} as const;
